import { Box, Button, Heading, HStack, Text, VStack } from '@chakra-ui/react'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import { useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import countryCodeToFlagEmoji from '@/utils/countryCodeToFlagEmoji'

import SeriesQ from '@/queries/SeriesQ'
import UserQ from '@/queries/UserQ'
import WorldQ from '@/queries/WorldQ'
import type { Query } from '@/client/query'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import type { SerializedRichText } from '@/client/services/richText/types'

import SeriesChatTutorsButton from '@/components/Series/SeriesChatTutorsButton'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import RichText from '@/components/UI/RichTextEditor/RichText'
import TruncatedRichText from '@/components/UI/RichTextEditor/TruncatedRichText'
import UserAvatar from '@/components/UserAvatar'
import { userWithMemberDetailsFromQuery } from '@/components/Worlds/transforms/detailsFromQueries'
import AvatarPopover from '@/components/Worlds/UI/Avatar/AvatarPopover'
import Card from '@/components/Worlds/UI/Card'

import SeriesHostCard from './SeriesHostCard'
import SeriesQuickLinks from './SeriesQuickLinks'

function HostRow({ host, world }: { host: Query; world: Query }) {
  const progressionLevel = host(UserQ.progression_level)
  const country = host(UserQ.country) ?? ''

  const memberUser = userWithMemberDetailsFromQuery({ worldMember: host })

  const worldDetails = {
    slug: world(WorldQ.slug) ?? '',
    title: world(WorldQ.title) ?? '',
  }

  return (
    <HStack justifyContent="space-between" alignItems="center">
      <AvatarPopover
        user={memberUser}
        worldSlug={worldDetails.slug}
        worldLevel={memberUser?.memberDetails?.level ?? null}
      >
        <HStack spacing="8px" cursor="pointer">
          <UserAvatar
            size="sm"
            src={memberUser.avatarUrl}
            name={memberUser.displayName}
            progressionLevel={progressionLevel}
          />
          <Text fontWeight="semibold">{memberUser.displayName}</Text>
          <Text>{countryCodeToFlagEmoji(country)}</Text>
        </HStack>
      </AvatarPopover>
    </HStack>
  )
}

function ShowMore({
  description,
  hosts,
  worldSlug,
}: {
  description: SerializedRichText
  hosts: Query[]
  worldSlug: string
}) {
  const [showModal, setShowModal] = useState(false)
  return (
    <>
      <ModalV3
        size="2xl"
        open={showModal}
        title={() => (
          <Heading size="xl" mt="8px">
            About
          </Heading>
        )}
        onClose={() => setShowModal(false)}
        removeBodyPaddingAndMargin
        body={() => (
          <VStack gap="32px" alignItems="stretch">
            <Box px="24px">
              <RichText content={description} />
            </Box>
            <VStack gap="16px" alignItems="stretch">
              <Heading size="lg" px={6}>
                Tutored by
              </Heading>
              <HStack gap="16px" overflowX="auto" alignItems="stretch" px={6} pb={6}>
                {hosts.map(host => (
                  <SeriesHostCard
                    key={`${host(UserQ.id)}`}
                    host={host}
                    worldSlug={worldSlug}
                    hasFixedWidth={hosts.length > 1}
                  />
                ))}
              </HStack>
            </VStack>
          </VStack>
        )}
      />
      <Button
        variant="ghost"
        justifyContent="flex-start"
        height="fit-content"
        rightIcon={<FontAwesomeIcon icon={faChevronRight} size="sm" />}
        onClick={() => setShowModal(true)}
      >
        Show more
      </Button>
    </>
  )
}

export default function SeriesParticipantSidebar({
  series,
  hosts,
  isUserRegisteredLearner,
}: {
  series: Query
  hosts: Query[]
  isUserRegisteredLearner: boolean
}) {
  const seriesId = series(SeriesQ.id) ?? 0
  const description = series(SeriesQ.description) ?? EMPTY_RICH_TEXT_VALUE
  const endDate =
    series(SeriesQ.closed_at) != null ? DateTime.fromISO(`${series(SeriesQ.closed_at)}`) : null

  const world = series.one(WorldQ.where({ id: SeriesQ.world_id }))
  const worldSlug = world(WorldQ.slug) ?? ''

  return (
    <VStack gap="24px" alignItems="stretch">
      <Card p="0px">
        <VStack p="0px" gap="0px" alignItems="stretch">
          <VStack
            gap="16px"
            alignItems="stretch"
            p="24px"
            pb="16px"
            borderBottom={`2px solid ${chakraTokenToVar('border')}`}
          >
            <Text variant="eyebrow" color="fg.subtle" textAlign="left">
              About
            </Text>
            <TruncatedRichText content={description} noOfLines={5} />
            <ShowMore description={description} hosts={hosts} worldSlug={worldSlug} />
          </VStack>
          <VStack p="24px" gap="16px" alignItems="stretch">
            <Text variant="eyebrow" color="fg.subtle" textAlign="left">
              Tutored By
            </Text>
            <VStack gap="12px" alignItems="stretch">
              {hosts.map(host => (
                <HostRow key={`${host(UserQ.id)}`} host={host} world={world} />
              ))}
            </VStack>
            {isUserRegisteredLearner ? (
              <SeriesChatTutorsButton
                seriesId={seriesId}
                seriesEndDate={endDate}
                countTutors={hosts.length}
              />
            ) : null}
          </VStack>
        </VStack>
      </Card>
      <SeriesQuickLinks series={series} />
    </VStack>
  )
}
